import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  ChartLineUp, Users, CurrencyEur, Ticket, ShieldCheck,
  CheckCircle, XCircle, ArrowsClockwise, PlugsConnected, Warning,
} from "@phosphor-icons/react";

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, h] = await Promise.all([
        api.get("/admin/analytics"),
        api.get("/admin/health"),
      ]);
      setData(a.data);
      setHealth(h.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user?.is_admin) load(); }, [user]);

  const syncPromos = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/sync-promotion-codes");
      const missing = data.result.filter((r) => r.status === "MISSING");
      if (missing.length === 0) toast.success("Tutti i codici sono attivi");
      else toast.error(`${missing.length} codici da creare su Stripe Dashboard`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore sync");
    } finally {
      setSyncing(false);
    }
  };

  if (authLoading) return <Layout><div className="text-[#A3A3A3] p-8 text-center">…</div></Layout>;
  if (!user?.is_admin) return <Navigate to="/dashboard" replace />;

  return (
    <Layout>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold">Console</div>
          <h1 className="font-heading text-4xl font-black uppercase tracking-tighter leading-none mt-1">
            Analytics Admin
          </h1>
        </div>
        <button data-testid="refresh-btn" onClick={load} disabled={loading}
          className="h-10 px-3 rounded-md border border-[#333] bg-[#1A1A1A] hover:border-[#FFD600] text-xs font-bold uppercase flex items-center gap-1 disabled:opacity-50">
          <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""}/> Aggiorna
        </button>
      </div>

      {loading && !data && <div className="text-[#A3A3A3] text-center py-12">Caricamento…</div>}

      {data && (
        <>
          {/* System status */}
          <Section title="Sistema" icon={ShieldCheck}>
            <div className="grid grid-cols-2 gap-2" data-testid="system-status">
              <StatusBadge label="Stripe API" value={health?.stripe_api_key} good={(v) => v === "live" || v === "test"} />
              <StatusBadge label="Webhook" value={health?.stripe_webhook_secret} good={(v) => v === "configured"} />
              <StatusBadge label="LLM key" value={health?.emergent_llm_key} good={(v) => v === "configured"} />
              <StatusBadge label="Admin email" value={health?.admin_email} good={(v) => v === "configured"} />
            </div>
            {health?.stripe_webhook_secret !== "configured" && (
              <div className="mt-3 text-xs text-[#FFAA00] bg-[#FFAA00]/10 border border-[#FFAA00]/30 rounded p-2 flex gap-2">
                <Warning size={16} weight="fill" className="shrink-0 mt-0.5"/>
                <span>Webhook secret mancante: gli eventi Stripe non sono verificati. Configura
                  <code className="text-[#FFD600]"> STRIPE_WEBHOOK_SECRET</code> in <code className="text-[#FFD600]">backend/.env</code>.</span>
              </div>
            )}
          </Section>

          {/* KPIs */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <KPI title="MRR" value={`€${data.stripe?.mrr_eur ?? 0}`} icon={CurrencyEur} highlight />
            <KPI title="Subs attive" value={data.stripe?.active_subscriptions ?? 0} icon={ChartLineUp} />
            <KPI title="In trial" value={data.stripe?.trialing_subscriptions ?? 0} icon={Users} />
            <KPI title="Utenti totali" value={data.users.total} icon={Users} />
          </div>

          {/* Subscriptions by plan */}
          <Section title="Iscrizioni per piano" icon={ChartLineUp}>
            <div className="space-y-2">
              {(data.plans_catalogue || []).map((p) => {
                const count = data.stripe?.subscriptions_by_plan?.[p.key] || 0;
                return (
                  <div key={p.key} className="bg-[#0A0A0A] rounded p-3 flex justify-between items-center">
                    <div>
                      <div className="font-bold uppercase text-sm">{p.key}</div>
                      <div className="text-xs text-[#A3A3A3]">€{p.amount_eur}/mese · {p.name}</div>
                    </div>
                    <div className="font-heading text-3xl font-black text-[#FFD600]">{count}</div>
                  </div>
                );
              })}
            </div>
          </Section>

          {/* Promotion codes */}
          <Section title="Codici sconto" icon={Ticket}>
            <button data-testid="sync-promos-btn" onClick={syncPromos} disabled={syncing}
              className="text-xs font-bold uppercase text-[#FFD600] border border-[#FFD600]/40 rounded h-8 px-3 mb-3 disabled:opacity-50">
              {syncing ? "Verifico…" : "Verifica PROMO30 & PARTNER50"}
            </button>
            <div className="space-y-2" data-testid="promos-list">
              {(data.stripe?.promotion_codes || []).length === 0 && (
                <div className="text-xs text-[#A3A3A3] bg-[#0A0A0A] rounded p-3">
                  Nessun codice promo attivo. Creane su <b className="text-[#FFD600]">Stripe Dashboard → Coupons → Promotion codes</b>.
                </div>
              )}
              {(data.stripe?.promotion_codes || []).map((p) => (
                <div key={p.code} className="bg-[#0A0A0A] rounded p-3" data-testid={`promo-${p.code}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-bold text-[#FFD600] uppercase">{p.code}</div>
                      <div className="text-xs text-[#A3A3A3] mt-0.5">
                        {p.percent_off ? `${p.percent_off}% di sconto` : (p.amount_off ? `${p.amount_off}€ di sconto` : "—")}
                        {p.duration ? ` · ${p.duration}` : ""}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-heading text-2xl font-black text-white">{p.times_redeemed}</div>
                      <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">utilizzi</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* Webhook status */}
          <Section title="Webhook Stripe" icon={PlugsConnected}>
            {(data.stripe?.webhook_endpoints || []).length === 0 ? (
              <div className="text-xs text-[#FFAA00] bg-[#FFAA00]/10 border border-[#FFAA00]/30 rounded p-3">
                Nessun webhook configurato. Vai su <b className="text-[#FFD600]">Stripe Dashboard → Developers → Webhooks</b>
                e aggiungi un endpoint che punta a <code className="text-[#FFD600]">https://etabuild.it/api/webhook/stripe</code>.
              </div>
            ) : (
              <div className="space-y-2">
                {data.stripe.webhook_endpoints.map((w) => (
                  <div key={w.url} className="bg-[#0A0A0A] rounded p-3 text-xs">
                    <div className="font-mono break-all text-white">{w.url}</div>
                    <div className="text-[#A3A3A3] mt-1">
                      Stato: <b className={w.status === "enabled" ? "text-[#00C853]" : "text-[#FF3B30]"}>{w.status}</b>
                      · {w.enabled_events_count} eventi · API {w.api_version}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* User status breakdown */}
          <Section title="Stati utenti" icon={Users}>
            <div className="space-y-1.5">
              {Object.entries(data.users.by_subscription_status).map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm bg-[#0A0A0A] rounded px-3 py-2">
                  <span className="uppercase tracking-wider text-xs font-bold text-[#A3A3A3]">{k}</span>
                  <span className="font-heading font-black text-[#FFD600]">{v}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <div className="bg-[#0A0A0A] rounded p-2 text-center">
                <div className="text-[#00C853] font-heading text-2xl font-black">{data.users.trial_active}</div>
                <div className="text-[#A3A3A3] uppercase tracking-wider">Trial attivi</div>
              </div>
              <div className="bg-[#0A0A0A] rounded p-2 text-center">
                <div className="text-[#FF3B30] font-heading text-2xl font-black">{data.users.trial_expired}</div>
                <div className="text-[#A3A3A3] uppercase tracking-wider">Trial scaduti</div>
              </div>
            </div>
          </Section>

          {/* App data */}
          <Section title="Dati applicativi" icon={ChartLineUp}>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {Object.entries(data.app_data).map(([k, v]) => (
                <div key={k} className="bg-[#0A0A0A] rounded p-3 flex justify-between">
                  <span className="uppercase tracking-wider text-xs font-bold text-[#A3A3A3]">{k}</span>
                  <span className="font-mono font-bold text-white">{v}</span>
                </div>
              ))}
            </div>
          </Section>

          <div className="text-xs text-[#666] text-center mt-4 mb-6">
            Aggiornato: {new Date(data.generated_at).toLocaleString("it-IT")}
          </div>
        </>
      )}
    </Layout>
  );
}

function KPI({ title, value, icon: Icon, highlight }) {
  return (
    <div className={`rounded-md p-4 border ${highlight ? "border-[#FFD600] bg-gradient-to-br from-[#FFD600]/15 to-[#FFD600]/0" : "border-[#333] bg-[#1A1A1A]"}`}
      data-testid={`kpi-${title}`}>
      <div className="flex items-center gap-1.5 text-[#A3A3A3] mb-1">
        <Icon size={14} weight="bold" />
        <span className="text-[10px] uppercase tracking-wider font-bold">{title}</span>
      </div>
      <div className="font-heading text-3xl font-black tracking-tighter">{value}</div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-4 mt-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={18} weight="bold" className="text-[#FFD600]"/>
        <h2 className="text-xs uppercase tracking-wider font-bold">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function StatusBadge({ label, value, good }) {
  const ok = good(value);
  return (
    <div className="bg-[#0A0A0A] rounded p-2 flex items-center justify-between">
      <span className="text-xs text-[#A3A3A3] uppercase tracking-wider font-bold">{label}</span>
      <span className={`flex items-center gap-1 text-xs font-bold ${ok ? "text-[#00C853]" : "text-[#FF3B30]"}`}>
        {ok ? <CheckCircle size={14} weight="fill"/> : <XCircle size={14} weight="fill"/>}
        {value || "—"}
      </span>
    </div>
  );
}
